export interface DetectionResult {
  isJuggling: boolean;
  throwDetected: boolean;
  dropDetected: boolean;
  motionScore: number;
  isCalibrating: boolean;
}

export class SimpleJugglingDetector {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D | null;
  private previousFrame: ImageData | null = null;
  private motionHistory: number[] = [];
  private historySize = 10;
  private lastThrowTime = 0;
  private throwCooldown = 500;
  private calibrationFrames: number;
  private frameCount = 0;
  private baselineMotion = 0;
  private calibrationSamples: number[] = [];
  private recentMotionScores: number[] = [];
  private motionScoreHistorySize = 5;

  constructor(calibrationSeconds: number = 3) {
    this.canvas = document.createElement('canvas');
    this.ctx = this.canvas.getContext('2d');
    this.calibrationFrames = calibrationSeconds * 60; // ~60fps
  }

  detect(video: HTMLVideoElement): DetectionResult {
    if (!this.ctx || video.videoWidth === 0 || video.videoHeight === 0) {
      return { isJuggling: false, throwDetected: false, dropDetected: false, motionScore: 0, isCalibrating: true };
    }

    this.canvas.width = video.videoWidth;
    this.canvas.height = video.videoHeight;
    this.ctx.drawImage(video, 0, 0);

    const currentFrame = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height);

    if (!this.previousFrame) {
      this.previousFrame = currentFrame;
      return { isJuggling: false, throwDetected: false, dropDetected: false, motionScore: 0, isCalibrating: true };
    }

    const motionScore = this.calculateCenterMotion(currentFrame, this.previousFrame);

    this.recentMotionScores.push(motionScore);
    if (this.recentMotionScores.length > this.motionScoreHistorySize) {
      this.recentMotionScores.shift();
    }

    if (this.frameCount < this.calibrationFrames) {
      this.calibrationSamples.push(motionScore);
      this.frameCount++;

      if (this.frameCount === this.calibrationFrames) {
        const minMotion = Math.min(...this.calibrationSamples);
        this.baselineMotion = Math.max(minMotion, 0.001);
      }

      this.previousFrame = currentFrame;
      return { isJuggling: false, throwDetected: false, dropDetected: false, motionScore, isCalibrating: true };
    }

    this.motionHistory.push(motionScore);
    if (this.motionHistory.length > this.historySize) {
      this.motionHistory.shift();
    }

    const avgMotion = this.motionHistory.reduce((a, b) => a + b, 0) / this.motionHistory.length;
    const isJuggling = avgMotion > this.baselineMotion + 0.003;

    const now = Date.now();
    let throwDetected = false;
    if (isJuggling && now - this.lastThrowTime > this.throwCooldown) {
      const threshold = Math.max(this.baselineMotion * 2.5, 0.015);
      if (motionScore > threshold) {
        throwDetected = true;
        this.lastThrowTime = now;
      }
    }

    this.previousFrame = currentFrame;

    return { isJuggling, throwDetected, dropDetected: false, motionScore, isCalibrating: false };
  }

  private calculateCenterMotion(current: ImageData, previous: ImageData): number {
    const marginX = Math.floor(current.width * 0.1);
    const marginY = Math.floor(current.height * 0.1);
    const startX = marginX;
    const endX = current.width - marginX;
    const startY = marginY;
    const endY = Math.floor(current.height * 0.8);

    let motionPixels = 0;
    let totalPixels = 0;
    const threshold = 25;

    for (let y = startY; y < endY; y++) {
      for (let x = startX; x < endX; x++) {
        const i = (y * current.width + x) * 4;
        const rDiff = Math.abs(current.data[i] - previous.data[i]);
        const gDiff = Math.abs(current.data[i + 1] - previous.data[i + 1]);
        const bDiff = Math.abs(current.data[i + 2] - previous.data[i + 2]);
        const diff = (rDiff + gDiff + bDiff) / 3;

        if (diff > threshold) {
          motionPixels++;
        }
        totalPixels++;
      }
    }

    return motionPixels / totalPixels;
  }

  getDetectionZone(videoWidth: number, videoHeight: number): { x: number; y: number; width: number; height: number } {
    const marginX = Math.floor(videoWidth * 0.1);
    const marginY = Math.floor(videoHeight * 0.1);
    return {
      x: marginX,
      y: marginY,
      width: videoWidth - marginX * 2,
      height: Math.floor(videoHeight * 0.8) - marginY,
    };
  }
}
